/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import APIHelper from "../../api/APIHelper";
import envConfig from "../../config/envConfig";
import { useTheme } from "../../theme/ThemeContext";
import Button from "../Button";
import ProgressBarDialog from "../common/ProgressBar";
import AlertDialog from "../common/AlertDialog";
import { convertUTCStringToLocalStandard, toDisplayString } from "../../utils/utils";

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

/**
 * Detects ISO 8601 datetime strings and converts them to local timezone.
 * Non-datetime values pass through unchanged.
 */
const formatAuditValue = (value) => {
    if (value === null || value === undefined) return value;
    // Objects (json, geo_point, etc.) -> safe string, never "[object Object]"
    if (typeof value === 'object') return toDisplayString(value);
    const str = String(value);
    // Match ISO datetime patterns: YYYY-MM-DDTHH:MM:SS or YYYY-MM-DD HH:MM:SS
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(str)) {
        const converted = convertUTCStringToLocalStandard(str);
        return converted || str;
    }
    return value;
};

/**
 * AuditHistoryModal
 * -----------------
 * A premium, responsive, timeline-style modal to display audit logs.
 */
export default function AuditHistoryModal({ show, onClose, filters = {} }) {
    const { theme } = useTheme();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;

    // For responsiveness
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (show && filters.organization_id) {
            fetchHistory(1);
        } else if (!show) {
            setLogs([]);
            setError(null);
            setPage(1);
        }
    }, [show, filters.organization_id, filters.application_id, filters.table_name, filters.record_id]);

    const fetchHistory = async (pageToFetch) => {
        setIsLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                organization_id: filters.organization_id,
                page: pageToFetch,
                page_size: pageSize
            });

            if (filters.application_id) queryParams.append('application_id', filters.application_id);
            if (filters.table_name) queryParams.append('table_name', filters.table_name);
            if (filters.record_id) queryParams.append('record_id', filters.record_id);

            const resp = await apiHelper.apiCall(`/arcee/audit/list?${queryParams.toString()}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            if (!resp.ok) {
                let errText = `Failed to fetch history (${resp.status})`;
                try {
                    const j = await resp.json();
                    if (j && j.error) errText = j.error;
                } catch (e) { }
                throw new Error(errText);
            }

            const data = await resp.json();
            setLogs(data.results || []);
            setTotalCount(data.count || 0);
            setPage(pageToFetch);

        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const renderDiff = (diff) => {
        if (!diff || typeof diff !== 'object') return <span className="text-muted" style={{ fontStyle: 'italic', fontSize: '13px' }}>No changes recorded</span>;

        const ignoredFields = ['created_by_id', 'updated_at', 'created_at'];
        const changes = Object.entries(diff).filter(([key]) => !ignoredFields.includes(key));

        if (changes.length === 0) return <span className="text-muted" style={{ fontSize: '13px' }}>No specific field changes.</span>;

        return (
            <div style={{
                background: '#fafafa',
                borderRadius: theme.cardRadius,
                padding: '12px 16px',
                border: '1px solid #f0f0f0',
                fontSize: '13px',
                marginTop: 8
            }}>
                {changes.map(([field, change], idx) => (
                    <div key={idx} style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '100%' : '120px 1fr',
                        gap: isMobile ? 4 : 12,
                        marginBottom: idx === changes.length - 1 ? 0 : 10,
                        alignItems: isMobile ? 'start' : 'center',
                        position: 'relative'
                    }}>
                        <div
                            title={field}
                            style={{
                                fontWeight: 600,
                                color: '#555',
                                letterSpacing: '0.01em',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {field}
                        </div>
                        <div style={{ width: '100%' }}>
                            {change && typeof change === 'object' && 'from' in change ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                    <div style={{
                                        color: '#ef4444',
                                        background: '#fef2f2',
                                        padding: '2px 8px',
                                        borderRadius: 6,
                                        fontSize: '12px',
                                        border: '1px solid #fee2e2',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {String(change.from === null ? 'Empty' : formatAuditValue(change.from))}
                                    </div>
                                    <i className="bi bi-arrow-right-short" style={{ color: '#9ca3af', fontSize: '16px' }}></i>
                                    <div style={{
                                        color: '#10b981',
                                        background: '#ecfdf5',
                                        padding: '2px 8px',
                                        borderRadius: 6,
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        border: '1px solid #d1fae5',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {String(change.to === null ? 'Empty' : formatAuditValue(change.to))}
                                    </div>
                                </div>
                            ) : (
                                <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                                    {JSON.stringify(change)}
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderDeletedRecord = (beforeData) => {
        if (!beforeData || typeof beforeData !== 'object') return <span className="text-muted" style={{ fontStyle: 'italic', fontSize: '13px' }}>Deleted record data unavailable</span>;

        return (
            <div style={{
                background: '#fff5f5',
                borderRadius: theme.cardRadius,
                padding: '12px 16px',
                border: '1px solid #ffebe9',
                fontSize: '13px',
                marginTop: 8
            }}>
                <div style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#c53030',
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    Deleted Data Snapshot
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 10 }}>
                    {Object.entries(beforeData).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: '11px', color: '#7f1d1d', fontWeight: 600 }}>{key}</span>
                            <span style={{ color: '#4a0f0f', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '12px' }}>
                                {value === null ? 'null' : String(formatAuditValue(value))}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }

        pages.push(1);

        let start = Math.max(2, page - 1);
        let end = Math.min(totalPages - 1, page + 1);

        if (page <= 2) {
            end = 4;
        }
        if (page >= totalPages - 1) {
            start = totalPages - 3;
        }

        if (start > 2) {
            pages.push('...');
        }

        for (let i = start; i <= end; i++) {
            if (i > 1 && i < totalPages) {
                pages.push(i);
            }
        }

        if (end < totalPages - 1) {
            pages.push('...');
        }

        if (totalPages > 1) {
            pages.push(totalPages);
        }

        return pages;
    };

    const startRecord = (page - 1) * pageSize + 1;
    const endRecord = Math.min(page * pageSize, totalCount);

    return (
        <>
            <Modal show={show} onHide={onClose} size="lg" centered scrollable className="premium-modal">
                <style>
                    {`
                        .premium-modal .modal-content {
                            border: none;
                            border-radius: ${theme.cardRadius};
                            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                            overflow: hidden;
                        }
                        .premium-modal .modal-header {
                            border-bottom: 1px solid #f3f4f6;
                            padding: 24px;
                            background: rgba(255, 255, 255, 0.9);
                            backdrop-filter: blur(8px);
                        }
                        /* Custom Red Close Button */
                        .premium-modal .btn-close {
                            background-color: #fee2e2;
                            opacity: 1;
                            color: #dc2626;
                            padding: 0.5rem;
                            border-radius: 50%;
                            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23dc2626'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e");
                            transition: all 0.2s;
                        }
                        .premium-modal .btn-close:hover {
                            background-color: #fecaca;
                            transform: rotate(90deg);
                        }
                        
                        .timeline-line {
                            position: absolute;
                            top: 24px;
                            bottom: 24px;
                            left: 24px;
                            width: 2px;
                            background: linear-gradient(to bottom, #e5e7eb 50%, transparent 100%);
                            z-index: 0;
                        }
                        @keyframes slideIn {
                            from { opacity: 0; transform: translateY(10px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                        .timeline-item {
                            animation: slideIn 0.3s ease-out forwards;
                        }
                        
                        /* Sleek Pagination Page Buttons */
                        .page-nav-btn {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            border: 1px solid transparent;
                            background: transparent;
                            color: #6b7280;
                            font-weight: 600;
                            font-size: 13px;
                            display: flex;
                            align-items: justify;
                            justify-content: center;
                            transition: all 0.2s;
                            padding-top: 5px;
                        }
                        .page-nav-btn:hover:not(:disabled) {
                            border-color: ${theme.primary};
                            color: ${theme.primary};
                            background: #f9fafb;
                        }
                        .page-nav-btn.active {
                            background: ${theme.primary};
                            color: white;
                            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                        }
                        
                        /* Arrow Buttons */
                        .arrow-nav-btn {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            border: 1px solid #e5e7eb;
                            background: white;
                            color: #374151;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            transition: all 0.2s;
                        }
                        .arrow-nav-btn:hover:not(:disabled) {
                            border-color: ${theme.primary};
                            color: ${theme.primary};
                            background: #f9fafb;
                        }
                        .arrow-nav-btn:disabled {
                            opacity: 0.5;
                            cursor: not-allowed;
                            background: #f3f4f6;
                            border-color: #f3f4f6;
                        }
                    `}
                </style>
                <Modal.Header closeButton>
                    <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginRight: 24 }}>
                            <Modal.Title style={{ fontWeight: 800, color: '#111827', fontSize: '22px', letterSpacing: '-0.02em' }}>
                                Activity Log
                            </Modal.Title>
                        </div>
                        {(filters.table_name || filters.record_id) && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                                {filters.table_name && (
                                    <span style={{
                                        background: '#EEF2FF', color: '#4F46E5', fontWeight: 600, fontSize: '12px',
                                        padding: '4px 10px', borderRadius: 20
                                    }}>
                                        TABLE: {filters.table_name}
                                    </span>
                                )}
                                {filters.record_id && (
                                    <span style={{
                                        background: '#F3F4F6', color: '#374151', fontWeight: 600, fontSize: '12px',
                                        padding: '4px 10px', borderRadius: 20
                                    }}>
                                        ID: {filters.record_id}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </Modal.Header>

                <Modal.Body style={{ padding: '24px', backgroundColor: '#fff' }}>
                    {error && (
                        <div className="alert alert-danger" role="alert" style={{ borderRadius: theme.cardRadius, fontSize: '14px' }}>
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {error}
                        </div>
                    )}

                    {!isLoading && logs.length === 0 && !error && (
                        <div className="text-center py-5">
                            <div style={{
                                width: 72, height: 72, background: '#F9FAFB', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
                                color: '#D1D5DB'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-clock" viewBox="0 0 16 16">
                                    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                                </svg>
                            </div>
                            <h6 style={{ color: '#111827', fontWeight: 700, fontSize: '16px' }}>No activity yet</h6>
                            <p className="text-muted small" style={{ maxWidth: 300, margin: '0 auto' }}>
                                Changes made to this record or table will appear here.
                            </p>
                        </div>
                    )}

                    {logs.length > 0 && (
                        <div style={{ position: 'relative', paddingLeft: isMobile ? 0 : 20 }}>
                            {!isMobile && <div className="timeline-line" />}

                            {logs.map((log, index) => {
                                const actionColor =
                                    log.action === 'INSERT' ? '#10b981' :
                                        log.action === 'UPDATE' ? '#3b82f6' :
                                            log.action === 'DELETE' ? '#ef4444' : '#6b7280';

                                const actionBg =
                                    log.action === 'INSERT' ? '#ecfdf5' :
                                        log.action === 'UPDATE' ? '#eff6ff' :
                                            log.action === 'DELETE' ? '#fef2f2' : '#f9fafb';

                                const actionIcon =
                                    log.action === 'INSERT' ? 'bi-plus-lg' :
                                        log.action === 'UPDATE' ? 'bi-pencil' :
                                            log.action === 'DELETE' ? 'bi-trash' : 'bi-activity';

                                let actionText = 'Unknown';
                                if (log.action === 'INSERT') actionText = 'Created';
                                else if (log.action === 'UPDATE') actionText = 'Updated';
                                else if (log.action === 'DELETE') actionText = 'Deleted';

                                return (
                                    <div
                                        key={log.id}
                                        className="timeline-item"
                                        style={{
                                            position: 'relative',
                                            marginBottom: 20,
                                            paddingLeft: isMobile ? 0 : 36,
                                            animationDelay: `${index * 50}ms`
                                        }}
                                    >
                                        {!isMobile && (
                                            <div style={{
                                                position: 'absolute', left: 0, top: 16,
                                                width: 10, height: 10, borderRadius: '50%',
                                                background: actionColor,
                                                boxShadow: `0 0 0 4px #fff`
                                            }} />
                                        )}

                                        <div style={{
                                            background: '#fff',
                                            borderRadius: theme.cardRadius,
                                            border: '1px solid #f3f4f6',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                            padding: 20,
                                            transition: 'all 0.2s ease',
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: isMobile ? 'flex-start' : 'center',
                                                flexDirection: isMobile ? 'column' : 'row',
                                                gap: 8,
                                                marginBottom: 16
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <div style={{
                                                        width: 36, height: 36, borderRadius: 10,
                                                        background: actionBg, color: actionColor,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '16px'
                                                    }}>
                                                        <i className={`bi ${actionIcon}`}></i>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '14px', lineHeight: 1.2 }}>
                                                            {log.changed_by_name || log.changed_by || 'System'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            {actionText}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#9ca3af',
                                                    fontWeight: 500,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6
                                                }}>
                                                    <i className="bi bi-calendar4-week"></i>
                                                    {convertUTCStringToLocalStandard(log.changed_at) || log.changed_at}
                                                </div>
                                            </div>

                                            {/* Changes Area */}
                                            {log.action === 'DELETE'
                                                ? renderDeletedRecord(log.before_data)
                                                : renderDiff(log.diff)
                                            }

                                            {/* Footer metadata */}
                                            {(!filters.record_id || !filters.table_name) && (
                                                <div style={{
                                                    marginTop: 12, paddingTop: 12,
                                                    borderTop: '1px solid #f9fafb',
                                                    fontSize: '11px', color: '#9ca3af',
                                                    display: 'flex', gap: 16, fontWeight: 500
                                                }}>
                                                    {!filters.table_name && <span>TABLE: {log.table_name}</span>}
                                                    {!filters.record_id && <span>ID: {log.record_id}</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>

                {totalCount > 0 && (
                    <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '16px 24px', background: '#fff' }}>
                        <div className="d-flex w-100 justify-content-between align-items-center">
                            <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>
                                Showing records {startRecord}-{endRecord} of {totalCount}
                            </span>
                            <div className="d-flex gap-1 align-items-center">
                                {/* Prev Button */}
                                <button
                                    className="arrow-nav-btn"
                                    disabled={page <= 1 || isLoading}
                                    onClick={() => fetchHistory(page - 1)}
                                >
                                    <i className="bi bi-chevron-left" style={{ fontSize: 14 }}></i>
                                </button>

                                {/* Page Numbers */}
                                {getPageNumbers().map((p, idx) => {
                                    if (p === '...') {
                                        return (
                                            <span key={idx} style={{ color: '#9ca3af', fontSize: '14px', padding: '0 4px' }}>
                                                ...
                                            </span>
                                        );
                                    }
                                    return (
                                        <button
                                            key={idx}
                                            className={`page-nav-btn ${p === page ? 'active' : ''}`}
                                            disabled={isLoading}
                                            onClick={() => fetchHistory(p)}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}

                                {/* Next Button */}
                                <button
                                    className="arrow-nav-btn"
                                    disabled={page >= totalPages || isLoading}
                                    onClick={() => fetchHistory(page + 1)}
                                >
                                    <i className="bi bi-chevron-right" style={{ fontSize: 14 }}></i>
                                </button>
                            </div>
                        </div>
                    </Modal.Footer>
                )}
            </Modal>

            <ProgressBarDialog
                open={isLoading}
                text_message="Loading activity..."
            />
        </>
    );
}
